import { normalizeGroups } from "./canvas-groups";
import type { Edge, Node } from "@xyflow/react";
import { getFormattingState, patchNodeFormatting, type FormattingChange } from "./node-formatting";

export type CanvasGraph = { nodes: Node[]; edges: Edge[] };
export type GraphCommand = (graph: CanvasGraph) => CanvasGraph;

// Validate only introduced identities/references. Legacy content remains loadable.
export function acceptGraph(before: CanvasGraph, candidate: CanvasGraph): CanvasGraph {
  for (const key of ["nodes", "edges"] as const) {
    const counts = new Map<string, number>();
    for (const item of before[key]) counts.set(item.id, (counts.get(item.id) ?? 0) + 1);
    const seen = new Map<string, number>();
    for (const item of candidate[key]) {
      const count = (seen.get(item.id) ?? 0) + 1;
      if (!item.id || count > Math.max(1, counts.get(item.id) ?? 0)) return before;
      seen.set(item.id, count);
    }
  }
  const ids = new Set(candidate.nodes.map(node => node.id));
  const previous = new Map(before.edges.map(edge => [edge.id, edge]));
  for (const edge of candidate.edges) {
    const old = previous.get(edge.id);
    if ((!old || old.source !== edge.source || old.target !== edge.target) &&
      (!ids.has(edge.source) || !ids.has(edge.target))) return before;
  }
  const removed = new Set(before.nodes.filter(node => !ids.has(node.id)).map(node => node.id));
  try { return normalizeGroups({ nodes: candidate.nodes, edges: candidate.edges.filter(edge => !removed.has(edge.source) && !removed.has(edge.target)) }); }
  catch { return before; }
}

export function insertElements(nodes: Node[] = [], edges: Edge[] = []): GraphCommand {
  return graph => {
    for (const [existing, added] of [[graph.nodes, nodes], [graph.edges, edges]] as const) {
      const ids = new Set(existing.map(item => item.id));
      for (const item of added) { if (ids.has(item.id)) return graph; ids.add(item.id); }
    }
    return acceptGraph(graph, { nodes: [...graph.nodes, ...nodes], edges: [...graph.edges, ...edges] });
  };
}

export function connectNodes(edge: Edge): GraphCommand {
  return graph => graph.edges.some(existing => existing.source === edge.source && existing.target === edge.target &&
    (existing.sourceHandle ?? null) === (edge.sourceHandle ?? null) && (existing.targetHandle ?? null) === (edge.targetHandle ?? null))
    ? graph : insertElements([], [edge])(graph);
}

export function removeElements(nodeIds: string[] = [], edgeIds: string[] = []): GraphCommand {
  const requestedNodes = new Set(nodeIds), edges = new Set(edgeIds);
  return graph => {
    // Callers expand groups first; explicit removal still honors each member's protection.
    const nodes = new Set(graph.nodes.filter(node => requestedNodes.has(node.id) && node.deletable !== false).map(node => node.id));
    return normalizeGroups({ nodes: graph.nodes.filter(node => !nodes.has(node.id)),
      // Incident edges must disappear with their endpoint, even when direct deletion is disabled.
      edges: graph.edges.filter(edge => (!edges.has(edge.id) || edge.deletable === false) && !nodes.has(edge.source) && !nodes.has(edge.target)) });
  };
}

export function patchNodeData(id: string, data: Record<string, unknown>): GraphCommand {
  return graph => ({ ...graph, nodes: graph.nodes.map(node => node.id === id ? { ...node, data: { ...node.data, ...data } } : node) });
}

export function formatNode(id: string, change: FormattingChange): GraphCommand {
  return graph => ({ ...graph, nodes: graph.nodes.map(node => {
    if (node.id !== id) return node;
    const current = getFormattingState([{ ...node, selected: true }], { bgColor: "", fontSize: 14 });
    if (change.value === (change.kind === "fill" ? current.color : current.size)) return node;
    return { ...node, data: patchNodeFormatting(node.type, node.data, change) };
  }) });
}

export function patchNodeGeometry(id: string, geometry: Pick<Partial<Node>, "position" | "width" | "height" | "style">): GraphCommand {
  return graph => ({ ...graph, nodes: graph.nodes.map(node => node.id === id ? { ...node, ...geometry,
    ...(geometry.style ? { style: { ...node.style, ...geometry.style } } : {}) } : node) });
}

export function patchEdge(id: string, patch: Pick<Partial<Edge>, "data" | "source" | "target" | "sourceHandle" | "targetHandle">): GraphCommand {
  return graph => acceptGraph(graph, { ...graph, edges: graph.edges.map(edge => edge.id === id ? { ...edge, ...patch,
    ...(patch.data ? { data: { ...edge.data, ...patch.data } } : {}) } : edge) });
}
